import { clientErrorHandler } from '@sm/utils';

import {
  getTeamMenuItemConfigs,
  TAB_IDS,
  ACCOUNT_TYPES,
  USER_ROLES,
  SSO_STATE,
  TEAM_MEMBERSHIP_TYPE,
  DEFAULT_MY_TEAM_MENU_SUB_ITEMS,
} from './teamMenuConstants';

// build the submenu items based on the current user package and role permissions
const _getSubMenuItems = ({ menuConfigs, utSource }) => {
  const menuItemConfigs = getTeamMenuItemConfigs(utSource);

  const menuItems = [
    menuConfigs.canAccess.teamSummaryTab &&
      menuItemConfigs[TAB_IDS.teamSummary],
    menuConfigs.canAccess.manageUsersTab &&
      menuItemConfigs[TAB_IDS.manageUsers],
    menuConfigs.canAccess.addUsersTab && menuItemConfigs[TAB_IDS.addUsers],
    menuConfigs.canAccess.adminDashboardTab &&
      menuItemConfigs[TAB_IDS.adminDashboard],
    menuConfigs.canAccess.divisionsTab && menuItemConfigs[TAB_IDS.divisions],
    menuConfigs.canAccess.workgroupsTab && menuItemConfigs[TAB_IDS.workgroups],
    menuConfigs.canAccess.libraryTab && menuItemConfigs[TAB_IDS.library],
    menuConfigs.canAccess.activityTab && menuItemConfigs[TAB_IDS.activity],
    menuConfigs.canAccess.offlineDevicesTab &&
      menuItemConfigs[TAB_IDS.offlineDevices],
    menuConfigs.canAccess.manageCreditsTab &&
      menuItemConfigs[TAB_IDS.manageCredits],
    menuConfigs.canAccess.settingsTab && menuItemConfigs[TAB_IDS.settings],
  ];
  return menuItems.filter(Boolean);
};

// spell out all the combination of userPackage/role
// to better understand what the user can and cant see in the ui
const _buildMenuConfigs = ({
  features,
  isSSOEnabled = false,
  isAdminImpersonated = false,
  divisionAdminsCanManageUsers = false,
}) => ({
  enterpriseAccount: {
    [`${USER_ROLES.accountOwner}`]: {
      canAccess: {
        ...DEFAULT_MY_TEAM_MENU_SUB_ITEMS,
        teamSummaryTab: true,
        manageUsersTab: true,
        addUsersTab: !isSSOEnabled, // admin cant add users via UI on SSO enabled account
        adminDashboardTab: features.adminDashboardEnabled,
        divisionsTab: features.divisionsEnabled,
        workgroupsTab: features.workgroupsEnabled,
        activityTab: features.auditLogsEnabled || isAdminImpersonated,
        offlineDevicesTab: features.offlineEnterpriseEnabled,
        manageCreditsTab: features.teamAudienceCreditsEnabled,
        settingsTab: true,
      },
    },
    [`${USER_ROLES.admin}`]: {
      canAccess: {
        ...DEFAULT_MY_TEAM_MENU_SUB_ITEMS,
        teamSummaryTab: true,
        manageUsersTab: true,
        addUsersTab: !isSSOEnabled, // admin cant add users via UI on SSO enabled account
        adminDashboardTab: features.adminDashboardEnabled,
        divisionsTab: features.divisionsEnabled,
        workgroupsTab: features.workgroupsEnabled,
        activityTab: features.auditLogsEnabled || isAdminImpersonated,
        offlineDevicesTab: features.offlineEnterpriseEnabled,
        settingsTab: true,
      },
    },
    [`${USER_ROLES.divisionAdmin}`]: {
      canAccess: {
        ...DEFAULT_MY_TEAM_MENU_SUB_ITEMS,
        manageUsersTab: divisionAdminsCanManageUsers,
        adminDashboardTab: features.adminDashboardEnabled,
        divisionsTab: features.divisionsEnabled,
        workgroupsTab: features.workgroupsEnabled,
        activityTab: features.auditLogsEnabled || isAdminImpersonated,
      },
    },
    [`${USER_ROLES.user}`]: {
      canAccess: {
        ...DEFAULT_MY_TEAM_MENU_SUB_ITEMS,
        workgroupsTab: features.workgroupsEnabled,
      },
    },
  },
  teamAccount: {
    [`${USER_ROLES.accountOwner}`]: {
      canAccess: {
        ...DEFAULT_MY_TEAM_MENU_SUB_ITEMS,
        teamSummaryTab: true,
        manageUsersTab: true,
        addUsersTab: true,
      },
    },
    [`${USER_ROLES.admin}`]: {
      canAccess: {
        ...DEFAULT_MY_TEAM_MENU_SUB_ITEMS,
        teamSummaryTab: true,
        manageUsersTab: true,
        addUsersTab: true,
      },
    },
    [`${USER_ROLES.user}`]: {
      canAccess: {
        ...DEFAULT_MY_TEAM_MENU_SUB_ITEMS,
      },
    },
  },
});

const _getMenuItems = ({ user, session, utSource }) => {
  const team = user.team || {};
  const teamMembership = user.teamMembership || {};
  // I will work on conforming the mobile and desktop calls because
  // session is passed in in 2 different ways currently
  const userSession = session || user.session;
  const isAdminImpersonated = userSession && userSession.isAdminImpersonation;
  const { isAdmin, isDivisionAdmin } = teamMembership;
  const isSSOEnabled = team.ssoState === SSO_STATE.REQUIRED;
  const isTeamOwner =
    teamMembership.memberType === TEAM_MEMBERSHIP_TYPE.ACCOUNT_OWNER;
  const features = user.features || {};
  const isEnterpriseAccount = teamMembership.isEnterprise;
  const isTeamAccount = !teamMembership.isEnterprise;
  const divisionAdminsCanManageUsers =
    user?.team?.settings?.divisionAdminsCanManageUsers ?? false;

  let userAccountType = '';
  if (isEnterpriseAccount) {
    // enterprise package 26 accounts
    userAccountType = ACCOUNT_TYPES.enterpriseAccount;
  } else if (isTeamAccount) {
    // self serve teams
    userAccountType = ACCOUNT_TYPES.teamAccount;
  }

  let userRole = '';
  if (isTeamOwner) {
    userRole = USER_ROLES.accountOwner;
  } else if (isAdmin) {
    userRole = USER_ROLES.admin;
  } else if (isDivisionAdmin) {
    userRole = USER_ROLES.divisionAdmin;
  } else {
    userRole = USER_ROLES.user;
  }

  const validUserAccountAndRole = !!userAccountType && !!userRole;
  if (validUserAccountAndRole) {
    try {
      const menuConfigs = _buildMenuConfigs({
        features,
        isSSOEnabled,
        isAdminImpersonated,
        divisionAdminsCanManageUsers,
      })[userAccountType][userRole];
      // return only menu items the user have access to based on their
      // package/role
      return _getSubMenuItems({ menuConfigs, utSource });
    } catch (e) {
      // This should not occur, however it is a fallback in case we introduce
      // some new user account type
      clientErrorHandler.logError(
        e,
        'Unexpected error while building My Team menu items',
        {
          userAccountType,
          userRole,
          features,
          isSSOEnabled,
          isAdminImpersonated,
        }
      );

      return [];
    }
  }
  return [];
};

const getMyTeamMenuItems = ({ user, session = {}, utSource = 'header' }) => {
  if (!user) {
    return [];
  }

  return _getMenuItems({ user, session, utSource });
};

export default getMyTeamMenuItems;
